// WHERE A BIT SITS INSIDE ITS KUBE-SPACE — the one statement of the sub-cell lattice.
// BYTE-IDENTICAL twin of functions/lib/bit-slot.mjs; functions/test/bit-slot.test.mjs fails if they drift. Both
// ends must agree exactly, because the client draws a bit from this and the server validates the same key with it.
//
// A bit is a 0.5 cube. Its centre snaps to a 0.25 lattice inside its cell: each axis is one of 0.25, 0.5, 0.75,
// giving 3 x 3 x 3 = 27 positions — the 8 corners, 12 edge-centres, 6 face-centres and the centre. Two facts make
// this lattice safe, and bit-slot.test.mjs proves both rather than asserting them:
//   * every legal bit is FULLY inside its own cell (0.25 - 0.25 = 0, 0.75 + 0.25 = 1), so a bit never straddles a
//     cell boundary and overlap is always a question about ONE cell;
//   * the 8 corners are mutually non-overlapping and no larger independent set exists, so "up to 8 per kube-space"
//     is exactly true.
// The pose a bit has TODAY — tangent-centred, one quarter-kube inward on the cell's own up axis — is already a
// lattice position (a face-centre). That is what makes migration free: a three-part localKey keeps meaning what it
// means, and legacySlot() names the slot it has always occupied. Nothing moves and nothing is back-filled.

export const BIT_SCALE = 0.5;          // a bit is half a kube on every edge; 8 fill a kube, hence bitOf and the 1/8 economy
export const SLOT_AXIS = 3;            // lattice steps per axis
export const SLOT_COUNT = 27;          // SLOT_AXIS ^ 3
export const SLOT_STEP = 0.25;         // lattice spacing, in cells
// Mining cost: a bit is an eighth of the kube it came from, so breaking one costs an eighth of that kube's time
// and energy. A full 8/8 cell therefore costs exactly what the kube costs — the geometry and the economy agree.
export const BIT_COST_FRACTION = 1 / 8;
// THE TURF SEAT (world version 9, turf.mjs TURF_SEAT): a fifth key part past the 27 bit seats, '…_28', where a cell's turf
// is written when it differs from what the seed grew. Not a bit seat: isSlot says no, and nothing seats a bit there.
export const TURF_SEAT = 28;
// THE SEEDLING SEAT (seedlings.mjs SEEDLING_SEAT, the owner's tree seedlings of Oct 22): '…_29' of the cell a seedling
// grows in, holding the seedling planted there (its chunk's `t` keeps its growth clock). Not a bit seat either.
export const SEEDLING_SEAT = 29;

// slot -> the three lattice steps (0,1,2) on x,y,z
export function slotToSteps(slot) {
    return [Math.floor(slot / (SLOT_AXIS * SLOT_AXIS)) % SLOT_AXIS, Math.floor(slot / SLOT_AXIS) % SLOT_AXIS, slot % SLOT_AXIS];
}
export function stepsToSlot(ix, iy, iz) { return (ix * SLOT_AXIS + iy) * SLOT_AXIS + iz; }
export function isSlot(slot) { return Number.isInteger(slot) && slot >= 0 && slot < SLOT_COUNT; }

// slot -> the bit's centre as an OFFSET inside its cell, each component one of 0.25 / 0.5 / 0.75.
export function slotOffset(slot, out) {
    const s = slotToSteps(slot), o = out || [0, 0, 0];
    o[0] = SLOT_STEP + s[0] * SLOT_STEP; o[1] = SLOT_STEP + s[1] * SLOT_STEP; o[2] = SLOT_STEP + s[2] * SLOT_STEP;
    return o;
}

// Two 0.5 bits overlap iff their centres are closer than 0.5 on EVERY axis. On this lattice the gaps are 0, 0.25
// or 0.5, so that reduces to "within one lattice step on all three" — the whole placement legality rule.
export function slotsOverlap(a, b) {
    const p = slotToSteps(a), q = slotToSteps(b);
    return Math.abs(p[0] - q[0]) < 2 && Math.abs(p[1] - q[1]) < 2 && Math.abs(p[2] - q[2]) < 2;
}

// CORNERS ARE THE DEFAULT. Only 8 of the 27 positions are corners, and they are the only arrangement that fills a
// kube-space: measured over the real overlap rule, a first bit placed on a corner still leaves room for 8, an
// edge-centre for 7, a face-centre for 5 — and the dead centre for ONE. So 19 of the 27 positions silently cost
// capacity, and with equal thirds a third of every axis landed on one by accident. Nothing on screen explains why
// a cell then refuses a second bit, which is exactly the confusion this lattice creates for anyone who cannot see
// it. The middle band is therefore NARROW: a centred bit is still reachable, but you have to mean it.
export const CENTRE_BAND = 0.075;   // half-width of the middle band, in cells — 15% of the axis, vs 42.5% per corner
// The slot nearest a point given in CELL-LOCAL coordinates (0..1 on each axis) — how the crosshair names a slot.
// Clamped, so a hit exactly on a face or an edge still lands on a legal position instead of off the lattice.
export function slotAtLocal(lx, ly, lz) {
    const q = (v) => (v < 0.5 - CENTRE_BAND) ? 0 : (v > 0.5 + CENTRE_BAND ? 2 : 1);
    return stepsToSlot(q(lx), q(ly), q(lz));
}

// The OUTWARD grid axis for a cell — the face a walker standing there stands on. Cell centres are half-integers,
// so no component is ever 0 and the sign is never degenerate. Must stay identical to cellUpFace in system-view.js.
export function cellUpFace(gx, gy, gz) {
    const cx = gx + 0.5, cy = gy + 0.5, cz = gz + 0.5;
    const ax = Math.abs(cx), ay = Math.abs(cy), az = Math.abs(cz);
    const ai = (ax >= ay && ax >= az) ? 0 : (ay >= az ? 1 : 2);
    return { ai, sign: (ai === 0 ? cx : ai === 1 ? cy : cz) >= 0 ? 1 : -1 };
}

// The slot a THREE-part (legacy) key occupies: tangent-centred, one step inward on the cell's own up axis. Derived
// from the address alone, exactly as the seat always was, so every client and the server agree with no metadata.
// ── WALKER GEOMETRY ────────────────────────────────────────────────────────────────────────────────────────────
// Where a seated bit's box sits inside its cell, expressed the way the on-world walker needs it. The walker works
// per FACE: an outward axis `ai` with a `sign`, and two tangent axes. Both of these are pure lattice arithmetic,
// so they live with the rest of the contract rather than in the walker, and can be tested without a renderer.

// How far the bit's OUTWARD surface sits from the cell's inward face, as the delta to add to a cell radius k to get
// the radius the feet stand at. A full cell yields exactly faceOff(sign) — 1 on a + face, 0 on a − face — so a bit
// that reaches the cell's outward face is indistinguishable from solid ground there, which is what it should be.
//   + face: outward is +grid, so the top is the box's high edge   ->  off + 0.5*BIT_SCALE   (0.5 .. 1.0)
//   − face: outward is −grid, so the top is the box's low edge    ->  0.5*BIT_SCALE − off   (−0.5 .. 0)
export function slotOutwardTop(slot, ai, sign) {
    const off = slotOffset(slot, [0, 0, 0])[ai], h = BIT_SCALE * 0.5;
    return sign > 0 ? off + h : h - off;
}

// Does this bit lie under the point (lu, lv), given in CELL-LOCAL tangent coordinates (0..1) for face axis `ai`?
// Inclusive on the edge: standing exactly on a bit's rim should be standing on the bit.
export function slotCoversTangent(slot, ai, lu, lv) {
    const off = slotOffset(slot, [0, 0, 0]), t = TANGENTS[ai], h = BIT_SCALE * 0.5, e = 1e-9;
    return Math.abs(lu - off[t[0]]) <= h + e && Math.abs(lv - off[t[1]]) <= h + e;
}
const TANGENTS = [[1, 2], [0, 2], [0, 1]];   // the two tangent axes for each outward axis (0=X, 1=Y, 2=Z)

export function legacySlot(gx, gy, gz) {
    const f = cellUpFace(gx, gy, gz), s = [1, 1, 1];      // 1 = the 0.5 centre step on each axis
    s[f.ai] = f.sign > 0 ? 0 : 2;                          // inward = toward the planet = the low step on a + face
    return stepsToSlot(s[0], s[1], s[2]);
}

// localKey <-> slot. Three parts is a legacy solo bit (or a kube); four is slot-addressed. Parsing returns the
// slot a reader should USE, so callers never special-case the legacy form.
export function parseLocalKey(localKey) {
    const p = String(localKey).split('_');
    if (p.length !== 3 && p.length !== 4) return null;
    const lx = Number(p[0]), ly = Number(p[1]), lz = Number(p[2]);
    if (!Number.isInteger(lx) || !Number.isInteger(ly) || !Number.isInteger(lz)) return null;
    if (p.length === 3) return { lx, ly, lz, slot: null, legacy: true };
    const slot = Number(p[3]);
    if (slot === TURF_SEAT && p[3] === '28') return { lx, ly, lz, slot, legacy: false, turf: true };   // the cell's turf (turf.mjs)
    if (slot === SEEDLING_SEAT && p[3] === '29') return { lx, ly, lz, slot, legacy: false, seedling: true };   // a seedling growing there (seedlings.mjs)
    if (!isSlot(slot)) return null;                        // BOUNDED: an unbounded 4th part would store a slot nothing can seat
    return { lx, ly, lz, slot, legacy: false };
}
export function makeLocalKey(lx, ly, lz, slot) {
    return (slot == null) ? `${lx}_${ly}_${lz}` : `${lx}_${ly}_${lz}_${slot}`;
}

// The 8 corner slots — the only arrangement that fills a kube-space completely.
export const CORNER_SLOTS = (() => { const a = []; for (const ix of [0, 2]) for (const iy of [0, 2]) for (const iz of [0, 2]) a.push(stepsToSlot(ix, iy, iz)); return a; })();

// A SLAB'S FOUR SEATS (the Solar Panel, Oct 3). A slab is one object lying on the cell's INWARD half, its floor for
// whoever stands on the cell's up face, and it is solid across exactly the four corner seats there: the walker stands on
// those (system-view.js cellSlotList). Derived from the address alone, as legacySlot is, so every client agrees.
export function slabSlots(gx, gy, gz) {
    const f = cellUpFace(gx, gy, gz), low = f.sign > 0 ? 0 : 2;   // inward = the low step on a + face, the high on a − face
    return CORNER_SLOTS.filter((s) => slotToSteps(s)[f.ai] === low);
}

// May a bit go in `slot`, given the cell's occupied slots (a Map slot -> name, or null for an empty cell)?
// Overlap, not equality: a face-centre bit occupies no corner, yet blocks four of them. Testing only for an exact
// clash would let two bits interpenetrate, which no other part of the system could then represent.
export function canPlaceSlot(slots, slot) {
    if (!isSlot(slot)) return false;
    if (!slots) return true;
    for (const s of slots.keys()) if (slotsOverlap(s, slot)) return false;
    return true;
}

// The bit name a cell is UNIFORMLY full of, or null. Only the 8 corners can fill a kube-space, so a full cell has
// exactly 8 entries — anything else would overlap one of them. The caller maps this through bitBaseKubeName to
// get the kube the cell collapses into; this module stays free of the catalogue.
export function uniformFullBit(slots) {
    if (!slots || slots.size !== CORNER_SLOTS.length) return null;
    let name = null;
    for (const c of CORNER_SLOTS) {
        const n = slots.get(c);
        if (n == null) return null;
        if (name === null) name = n; else if (n !== name) return null;
    }
    return name;
}

// Apply one slot-addressed edit to a cell's slot map. PURE: returns a NEW map (or null when the cell empties),
// or the string `false` sentinel REJECT when the edit is not legal. Kept here, not in the renderer's closure, so
// the transition can be tested without a browser — and so the client and the server reason about a cell the same
// way, which is the property that made the old one-object-per-cell server drift possible in the first place.
//   slots  Map<slot,name> | null   the cell's current slot-addressed bits
//   slot   0..26
//   name   the bit to place, or null to break that slot
export const REJECT = 'reject';
export function applySlotEdit(slots, slot, name) {
    if (!isSlot(slot)) return REJECT;
    const cur = slots ? new Map(slots) : new Map();
    if (name == null) {                       // break one slot
        if (!cur.has(slot)) return REJECT;    // nothing there — the caller must not charge for it
        cur.delete(slot);
        return cur.size ? cur : null;
    }
    if (cur.has(slot)) return REJECT;         // occupied
    for (const s of cur.keys()) if (slotsOverlap(s, slot)) return REJECT;   // or overlapped by a neighbour
    cur.set(slot, name);
    return cur;
}
